import { useNavigate, useParams } from 'react-router-dom';
import { wines } from '../data/wines';
import { getVisibleWines, findWineBySlug, getClassificationColor, formatPrice } from '../utils/wineHelpers';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useWineImageZoom } from '../hooks/useWineImageZoom';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import WineImageViewer from './wine/WineImageViewer';
import WineImageFullscreenModal from './wine/WineImageFullscreenModal';
import WineTechnicalSection from './wine/WineTechnicalSection';

const WINE_DETAIL_ANIMATIONS = `
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes zoomIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
`;

const WineDetails = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const wine = findWineBySlug(getVisibleWines(wines), slug);
  const zoom = useWineImageZoom(slug);

  useScrollToTop(slug);

  const technical = wine?.technicalDetails ?? {};
  const pairings = wine?.pairings ?? [];

  if (!wine) {
    return (
      <div className="min-h-full bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Vinho não encontrado</p>
          <button
            onClick={() => navigate('/')}
            className="text-wine-gold hover:text-white transition-colors"
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark py-8 px-4 tablet-landscape:py-4">
      <div className="max-w-5xl w-full mx-auto tablet-landscape:max-w-6xl">
        <div className="flex items-center mb-8 tablet-landscape:mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white hover:text-wine-gold transition-colors"
          >
            <ArrowLeftIcon />
            <span className="text-lg">Voltar</span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 tablet-landscape:gap-6 items-center tablet-landscape:items-start">
          <WineImageViewer wine={wine} {...zoom} />

          <div className="text-white space-y-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-serif mb-4">{wine.name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1.5 rounded-full text-sm font-semibold uppercase ${getClassificationColor(wine.classification)}`}>
                  {wine.classification}
                </span>
                <div className="h-6 w-px bg-gray-600" />
                <p className="text-wine-gold text-3xl font-bold">{formatPrice(wine.price)}</p>
              </div>
              <p className="text-gray-400 text-lg">
                <span className="text-wine-gold">Região:</span> {wine.region} • <span className="text-wine-gold">Uva:</span> {wine.grape}
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#2a2535] to-[#1a1625] rounded-2xl p-6 border border-gray-700/50">
              <h3 className="text-wine-gold text-sm font-semibold uppercase tracking-wide mb-4">
                🍽️ Harmoniza com
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {pairings.map((pairing, index) => (
                  <span
                    key={index}
                    className="bg-wine-dark px-4 py-2 rounded-full text-white text-sm border border-wine-gold/30 hover:border-wine-gold/60 transition-colors"
                  >
                    {pairing}
                  </span>
                ))}
              </div>
              {wine.pairingDescription && (
                <div className="mt-4 pt-4 border-t border-gray-700/50">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    &quot;{wine.pairingDescription}&quot;
                  </p>
                </div>
              )}
            </div>

            {wine.description && (
              <div className="bg-gradient-to-br from-[#2a2535] to-[#1a1625] rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-wine-gold text-sm font-semibold uppercase tracking-wide mb-3">
                  Sommelier Pedro Wilson
                </h3>
                <p className="text-gray-300 text-base leading-relaxed">{wine.description}</p>
              </div>
            )}

            <WineTechnicalSection technical={technical} />
          </div>
        </div>
      </div>

      <WineImageFullscreenModal wine={wine} {...zoom} />
      <style>{WINE_DETAIL_ANIMATIONS}</style>
    </div>
  );
};

export default WineDetails;
